/**
 * 日志工具
 * - production：全部静默
 * - 默认开发：仅输出 warn / error（避免行情/接口数据刷屏）
 * - 需要详细调试时设置 VITE_ENABLE_DEBUG_LOG=true
 */

interface Logger {
  log: (...args: any[]) => void
  info: (...args: any[]) => void
  warn: (...args: any[]) => void
  error: (...args: any[]) => void
  debug: (...args: any[]) => void
  group: (label?: string) => void
  groupEnd: () => void
}

/** 空函数（禁用时使用） */
const noop = () => {}

/**
 * 是否为生产环境（仅 MODE=production；测试包仍可看 warn）
 */
const checkIsProd = (): boolean => {
  try {
    // 优先构建注入的环境
    if (typeof __APP_ENV__ !== 'undefined' && __APP_ENV__ === 'production') {
      return true
    }
    const envMode = import.meta.env.MODE || import.meta.env.VITE_APP_ENV
    return envMode === 'production'
  } catch {
    return false
  }
}

/**
 * 是否开启详细调试日志（接口/行情等）
 */
const checkVerbose = (): boolean => {
  try {
    return String(import.meta.env.VITE_ENABLE_DEBUG_LOG || '') === 'true'
  } catch {
    return false
  }
}

const canVerbose = (): boolean => !checkIsProd() && checkVerbose()
const canWarn = (): boolean => !checkIsProd()

export const logger: Logger = {
  log: (...args: any[]) => (canVerbose() ? console.log(...args) : noop()),
  info: (...args: any[]) => (canVerbose() ? console.info(...args) : noop()),
  warn: (...args: any[]) => (canWarn() ? console.warn(...args) : noop()),
  error: (...args: any[]) => (canWarn() ? console.error(...args) : noop()),
  debug: (...args: any[]) =>
    canVerbose() ? (console.debug?.(...args) ?? console.log(...args)) : noop(),
  group: (label?: string) => (canVerbose() ? console.group?.(label) : noop()),
  groupEnd: () => (canVerbose() ? console.groupEnd?.() : noop()),
}

/**
 * 带标签的日志
 * @param tag 前缀标签
 */
export const createTaggedLogger = (tag: string) => {
  const prefix = `[${tag}]`

  return {
    log: (...args: any[]) => logger.log(prefix, ...args),
    info: (...args: any[]) => logger.info(prefix, ...args),
    warn: (...args: any[]) => logger.warn(prefix, ...args),
    error: (...args: any[]) => logger.error(prefix, ...args),
    debug: (...args: any[]) => logger.debug(prefix, ...args),
  }
}

export default logger
